import { Box, Button, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import StatusChip from "components/StatusChip";

import { KIND_LABEL, ServiceDetail } from "./types";
import { relativeTime, uptime } from "./components/format";
import ICAPPanel from "./panels/ICAPPanel";
import AlertPanel from "./panels/AlertPanel";
import ConnectivityPanel from "./panels/ConnectivityPanel";
import TrendsPanel from "./panels/TrendsPanel";
import AvailabilityPanel from "./panels/AvailabilityPanel";
import IndexesPanel from "./panels/IndexesPanel";

const REFRESH_INTERVAL_MS = 15_000;

export default function ServiceDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const login = useLogin();
    const notification = useNotification();

    const [detail, setDetail] = useState<ServiceDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const req = login.GetAxios();
    const reqRef = useRef(req);
    reqRef.current = req;

    useEffect(() => {
        let active = true;
        let inFlight = false;
        const load = async () => {
            const r = reqRef.current;
            // Skip if a previous poll is still pending, so a slow older response can't
            // overwrite newer service metrics.
            if (!r || !id || inFlight) return;
            inFlight = true;
            try {
                const resp = await r.get<ServiceDetail>(`/api/services/${encodeURIComponent(id)}`);
                if (!active) return;
                setDetail(resp.data);
                setNotFound(false);
                setLoading(false);
            } catch (err) {
                if (!active) return;
                if (axios.isAxiosError(err) && err.response) {
                    if (err.response.status === 404) { setNotFound(true); setLoading(false); return; }
                }
                notification.Display({ title: "Error", message: "Could not fetch service detail", type: "error" });
                setLoading(false);
            } finally {
                inFlight = false;
            }
        };
        load();
        const handle = window.setInterval(load, REFRESH_INTERVAL_MS);
        return () => { active = false; window.clearInterval(handle); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const metrics = detail?.metrics;
    const isShield = !!detail?.kind.startsWith("shield-");
    // The activity database owns the versioned indices, so the lifecycle panel
    // belongs on its row and nowhere else.
    const isSearch = detail?.kind === "elasticsearch" || detail?.kind === "opensearch";

    return <>
        <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="10px">
            <Stack direction="row" alignItems="center" gap={2}>
                <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate("/services")}>Services</Button>
                <Typography variant="h1">{detail?.instanceName || id}</Typography>
            </Stack>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>Refresh</Button>
        </Box>

        {loading && <LinearProgress />}

        {notFound && !loading && (
            <Card variant="outlined"><CardContent>
                <Typography>Service <b>{id}</b> was not found. It may have gone offline.</Typography>
            </CardContent></Card>
        )}

        {detail && !notFound && (
            <>
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" gap={4} flexWrap="wrap" alignItems="center">
                            <Box><Typography variant="subtitle2" color="text.secondary">Type</Typography>
                                <Typography>{KIND_LABEL[detail.kind] || detail.kind}</Typography></Box>
                            <Box><Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                <StatusChip status={detail.status} /></Box>
                            {detail.version && <Box><Typography variant="subtitle2" color="text.secondary">Version</Typography>
                                <Typography>{detail.version}</Typography></Box>}
                            {!!detail.rulesetVersion && <Box><Typography variant="subtitle2" color="text.secondary">Ruleset</Typography>
                                <Typography>v{detail.rulesetVersion}<Typography component="span" variant="caption" color="text.secondary"> · updated {relativeTime(detail.rulesetUpdatedAt)}</Typography></Typography></Box>}
                            {metrics?.startedAt && <Box><Typography variant="subtitle2" color="text.secondary">Uptime</Typography>
                                <Typography>{uptime(metrics.startedAt)}</Typography></Box>}
                            {metrics?.collectedAt && <Box><Typography variant="subtitle2" color="text.secondary">Metrics as of</Typography>
                                <Typography>{relativeTime(metrics.collectedAt)}</Typography></Box>}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{detail.description}</Typography>
                    </CardContent>
                </Card>

                {metrics?.icap && <ICAPPanel m={metrics.icap} />}
                {metrics?.alert && <AlertPanel m={metrics.alert} />}
                {detail.redis && <ConnectivityPanel title="Redis connectivity" hint="dashboard → Redis" r={detail.redis} />}
                {detail.search && <ConnectivityPanel title="Search connectivity" hint="activity database" r={detail.search} />}

                {isSearch && req && <IndexesPanel req={req} />}

                {isShield && req && <AvailabilityPanel req={req} id={detail.id} />}

                {isShield && req && (
                    <TrendsPanel req={req} id={detail.id} hasICAP={!!metrics?.icap} />
                )}

                {isShield && !metrics && (
                    <Card variant="outlined" sx={{ mt: 2 }}><CardContent>
                        <Typography color="text.secondary">Detailed metrics are not reported by this node.</Typography>
                    </CardContent></Card>
                )}
            </>
        )}
    </>;
}
